import { IconActionFile } from "@/components/Icons";
import { LearningOpportunity } from "@/server/backend-api";
import {
  UserAdmissionData,
  UserAdmissionFile,
} from "@/server/backend-api/learningOpportunityUserAdmission";
import { formatDate, getDateMonthShort } from "@/utils/dateUtils";
import { mcn } from "@/utils/mergeClassNames";
import { PropsWithChildren } from "react";
import { AdmissionProcessStaticData } from "../../_staticContent/admission";
import { LearningOpportunityUserStatus } from "@/server/backend-api/generated";

const ResponseButton = (props: { text: string; onClick?: () => void }) => {
  return (
    <button
      className={mcn(
        "text-style-subtitle1 underline uppercase",
        "hover:no-underline",
        "p-3xs rounded mr-md",
        "focus-visible:default-focus"
      )}
      onClick={props.onClick ?? (() => {})}
    >
      {props.text}
    </button>
  );
};

export type AdmissionCardProps = PropsWithChildren<{
  date?: string | null;
  status: LearningOpportunityUserStatus;
  userAdmission: UserAdmissionData;
  userAdmissionFiles?: UserAdmissionFile[];
  onAcceptOfferClick?: () => void;
  onDeclineOfferClick?: () => void;
}>;

const getTexts = (
  status: LearningOpportunityUserStatus,
  userAdmission: LearningOpportunity["userAdmissionData"]
) => {
  const header =
    AdmissionProcessStaticData.admissionProgressHeader[status] ?? "";
  let text = AdmissionProcessStaticData.admissionProgressText[status] ?? "";

  if (status === LearningOpportunityUserStatus.OfferedToLearner) {
    text = `${text} ${formatDate("long", userAdmission?.offerDueDate, true)}.`;
  }

  return { header, text };
};

export const AdmissionProgressCard = (props: AdmissionCardProps) => {
  const { status, date: dateStr, userAdmission, userAdmissionFiles } = props;
  const { header, text } = getTexts(status, userAdmission);

  const date = dateStr ? new Date(dateStr) : undefined;
  const day = date?.getDate();
  const month = getDateMonthShort(date);
  const year = date?.getFullYear();

  const isWaitingForLearnerOfferResponse =
    status === LearningOpportunityUserStatus.OfferedToLearner &&
    userAdmission.status === LearningOpportunityUserStatus.OfferedToLearner;
  const showFiles = status === LearningOpportunityUserStatus.LearnerApplied;

  return (
    <div
      className={mcn(
        "bg-light-blue text-white flex rounded-2",
        "flex-col tablet-lg:flex-row",
        "shadow-elevation-1",

        // bg color if first card based on status
        isWaitingForLearnerOfferResponse &&
          "first:bg-light-yellow first:text-text-primary",
        [
          LearningOpportunityUserStatus.LearnerAttending,
          LearningOpportunityUserStatus.LearnerCompleted,
        ].includes(status) && "first:bg-light-green first:text-text-primary",
        [
          LearningOpportunityUserStatus.LearnerRejectedOffer,
          LearningOpportunityUserStatus.OfferToLearnerExpired,
          LearningOpportunityUserStatus.AdmissionOfficerDeniedApplication,
        ].includes(status) && "first:bg-light-red first:text-text-primary"
      )}
    >
      <div
        className={mcn(
          "hidden tablet-lg:flex flex-col items-center",
          "bg-blue text-white",
          "rounded-l-2",
          "w-24 py-md flex-shrink-0"
        )}
      >
        <span className="text-style-body2">{month}</span>
        <span className="text-style-headline2">{day}</span>
        <span className="text-style-body2">{year}</span>
      </div>
      <div
        className={mcn(
          "tablet-lg:hidden",
          "flex flex-row items-center gap-xs",
          "bg-blue text-white",
          "px-lg py-md flex-shrink-0",
          "rounded-t-2"
        )}
      >
        <span className="text-style-subtitle1">
          {month} {day}
        </span>
        <span className="text-style-body2">{year}</span>
      </div>
      <div className="p-lg space-y-2xs overflow-hidden">
        <h3 className="text-style-headline5 flex items-center">
          {isWaitingForLearnerOfferResponse && (
            <span
              className={mcn(
                "w-md h-md mr-xs block rounded-full bg-yellow-100",
                "after:w-md after:h-md after:block after:rounded-full after:bg-yellow-100 after:animate-ping"
              )}
            ></span>
          )}
          {header}
        </h3>
        <p className="text-style-body2">{text}</p>

        {showFiles && !!userAdmissionFiles?.length && (
          <div>
            <div className="text-style-subtitle1 mt-lg mb-xs"> Details</div>
            <div className="flex flex-col gap-y-1">
              {userAdmissionFiles.map((file, index) => (
                <div
                  key={index}
                  className={mcn(
                    "flex items-center",
                    "text-style-subtitle1",
                    "rounded-1",
                    "py-3 px-2",
                    "bg-grey-10/10",
                    "overflow-hidden"
                  )}
                >
                  <span
                    className={mcn(
                      "bg-light-yellow text-light-blue",
                      "w-[32px] h-[32px] rounded-full",
                      "flex items-center justify-center flex-shrink-0",
                      "mr-4"
                    )}
                  >
                    <IconActionFile fontSize={20} />
                  </span>
                  <span className="flex-grow min-w-0 mr-2xs truncate">
                    {file.documentTypeName}
                  </span>
                  <a
                    href={`/api/userDocument/admission/${
                      userAdmission.admissionId
                    }/${encodeURIComponent(file.name)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="h-10 flex items-center px-2 py-1 text-style-body1 bg-white/10 hover:bg-white/20 text-white rounded-2"
                  >
                    View
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {isWaitingForLearnerOfferResponse && (
          <div className="text-right">
            <ResponseButton
              text="Decline"
              onClick={props.onDeclineOfferClick}
            />
            <ResponseButton text="Accept" onClick={props.onAcceptOfferClick} />
          </div>
        )}
      </div>
    </div>
  );
};
